import * as React from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Alert, FormGroup, Input, Label } from 'reactstrap';
import TaskCompleteDialog from '../../components/Tasks/TaskCompleteDialog';
import TaskCreateModal from '../../components/Tasks/TaskCreateModal';
import TaskItemsTable, { TaskRowEditState } from '../../components/Tasks/TaskItemsTable';
import {
    TaskItemData,
    TaskItemStatus,
    UpdateTaskItemData,
    UserData,
} from '../../models';
import {
    getTaskAssignableUsersAsync,
    getTasksAsync,
    GetTasksParams,
    updateTaskAsync,
    updateTaskStatusAsync,
} from '../../ajax/requests';
import { getEntitiesDataItems } from '../../store/entities/selectors';
import { ClientEntity, ProfessionalEntity, UserEntity } from '../../store/entities/state';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { getMyUser } from '../../store/session/selectors';
import {
    canCreateTasks,
    getTaskDismissReasonOptions,
    hasExtendedTaskManagementScope,
    sortTasksByStatusThenDueDate,
    TaskDismissReasons,
} from '../Tasks/taskUtils';
import { getTasksUpdatedAt } from '../../store/general/selectors';
import { paths } from '../../app/constants';
import AppButton from '../../components/AppButton';

export interface ProfessionalTasksTableProps {
    isOpen: boolean;
    professional: ProfessionalEntity;
}

const ProfessionalTasksTable: React.FC<ProfessionalTasksTableProps> = ({ isOpen, professional }) => {
    const t = useTranslations();
    const history = useHistory();
    const user = useSelector(getMyUser);
    const tasksUpdatedAt = useSelector(getTasksUpdatedAt);
    const usersInStore = useSelector(getEntitiesDataItems<UserEntity>('users'));
    const clients = useSelector(getEntitiesDataItems<ClientEntity>('clients'));
    const dismissReasonOptions = React.useMemo(() => getTaskDismissReasonOptions(), []);
    const linkedUser = React.useMemo(
        () => usersInStore.find(item => item.professionalId === professional.id),
        [professional.id, usersInStore],
    );

    const [tasks, setTasks] = React.useState<TaskItemData[]>([]);
    const [users, setUsers] = React.useState<UserData[]>([]);
    const [isLoading, setLoading] = React.useState(false);
    const [completeTask, setCompleteTask] = React.useState<TaskItemData | null>(null);
    const [dismissTask, setDismissTask] = React.useState<TaskItemData | null>(null);
    const [dismissReason, setDismissReason] = React.useState<string>(TaskDismissReasons.notApplicable);
    const [dismissOtherReason, setDismissOtherReason] = React.useState('');
    const [dismissNotes, setDismissNotes] = React.useState('');
    const [rowEdit, setRowEdit] = React.useState<TaskRowEditState | null>(null);
    const [isSavingRow, setSavingRow] = React.useState(false);
    const [showCreate, setShowCreate] = React.useState(false);
    const editingRowRef = React.useRef<HTMLTableRowElement | null>(null);

    const canCreateTaskItems = canCreateTasks(user);
    const hasScopedManagementAccess = React.useMemo(() => hasExtendedTaskManagementScope(user, users), [user, users]);

    const loadTasks = React.useCallback(async () => {
        if (!linkedUser?.id) {
            setTasks([]);
            return;
        }

        const dateFrom = moment().subtract(7, 'days').format('YYYY-MM-DD');
        const dateTo = moment().add(1, 'month').format('YYYY-MM-DD');

        setLoading(true);
        try {
            const params: GetTasksParams = {
                assignedUserId: linkedUser.id,
                dateFrom,
                dateTo,
                limit: 300,
                offset: 0,
            };
            const result = await getTasksAsync(params);
            setTasks(sortTasksByStatusThenDueDate(result));
        } finally {
            setLoading(false);
        }
    }, [linkedUser?.id]);

    React.useEffect(() => {
        if (!isOpen || !linkedUser?.id) return;
        loadTasks();
    }, [isOpen, linkedUser?.id, loadTasks, tasksUpdatedAt]);

    React.useEffect(() => {
        if (!canCreateTaskItems || !isOpen) {
            setUsers([]);
            return;
        }

        getTaskAssignableUsersAsync()
            .then(scopeUsers => {
                if (scopeUsers.length > 0) {
                    setUsers(scopeUsers);
                    return;
                }

                setUsers(user ? [user] : []);
            })
            .catch(() => setUsers(user ? [user] : []));
    }, [canCreateTaskItems, isOpen, user]);

    const startRowEdit = (task: TaskItemData) => {
        setRowEdit({
            taskId: task.id,
            clientId: task.clientId,
            title: task.title || '',
            dueDate: moment(task.dueDate).format('YYYY-MM-DDTHH:mm'),
        });
    };

    const saveRowEdit = React.useCallback(async (task: TaskItemData, editState: TaskRowEditState) => {
        const normalizedClientId = editState.clientId || undefined;
        const normalizedTitle = editState.title.trim() || undefined;
        const nextDueDate = editState.dueDate ? new Date(editState.dueDate) : new Date(task.dueDate);

        if (Number.isNaN(nextDueDate.getTime())) {
            return;
        }

        const isDetailsChanged = normalizedClientId !== (task.clientId || undefined)
            || normalizedTitle !== (task.title || undefined)
            || nextDueDate.getTime() !== new Date(task.dueDate).getTime();

        if (!isDetailsChanged) {
            setRowEdit(prev => prev?.taskId === editState.taskId ? null : prev);
            return;
        }

        setSavingRow(true);
        try {
            const payload: UpdateTaskItemData = {
                clientId: normalizedClientId,
                title: normalizedTitle,
                dueDate: nextDueDate.toISOString(),
            };
            await updateTaskAsync(task.id, payload);
            await loadTasks();
            setRowEdit(prev => prev?.taskId === editState.taskId ? null : prev);
        } finally {
            setSavingRow(false);
        }
    }, [loadTasks]);

    const handleSaveRowEdit = React.useCallback(async (task: TaskItemData) => {
        if (!rowEdit || rowEdit.taskId !== task.id) return;
        await saveRowEdit(task, rowEdit);
    }, [rowEdit, saveRowEdit]);

    React.useEffect(() => {
        if (!rowEdit || isSavingRow) {
            return;
        }

        const onDocumentMouseDown = (event: MouseEvent) => {
            if (editingRowRef.current?.contains(event.target as Node)) {
                return;
            }

            const editingTask = tasks.find(task => task.id === rowEdit.taskId);
            if (!editingTask) {
                setRowEdit(prev => prev?.taskId === rowEdit.taskId ? null : prev);
                return;
            }

            void saveRowEdit(editingTask, rowEdit);
        };

        document.addEventListener('mousedown', onDocumentMouseDown);
        return () => document.removeEventListener('mousedown', onDocumentMouseDown);
    }, [isSavingRow, rowEdit, saveRowEdit, tasks]);

    const navigateToClient = (clientId?: string) => {
        if (!clientId) return;
        history.push({
            pathname: paths.clients(),
            search: `?id=${clientId}`,
        });
    };

    const handleDismissTask = async (task: TaskItemData) => {
        const reason = dismissReason === TaskDismissReasons.other
            ? dismissOtherReason
            : dismissReason;
        await updateTaskStatusAsync(task.id, {
            status: TaskItemStatus.Dismissed,
            dismissReason: reason || undefined,
            notes: dismissNotes || undefined,
        });
        setDismissTask(null);
        setDismissReason(TaskDismissReasons.notApplicable);
        setDismissOtherReason('');
        setDismissNotes('');
        await loadTasks();
    };

    if (!linkedUser) {
        return (
            <Alert color="info" style={{ marginBottom: 0 }}>
                A professional must have a user associated to use tasks.
            </Alert>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
            {canCreateTaskItems && (
                <div className="topButtonsContainer">
                    <AppButton size="sm" color="success" onClick={() => setShowCreate(true)}>
                        {t(TK.createTask)}
                    </AppButton>
                </div>
            )}

            <TaskItemsTable
                tasks={tasks}
                user={user}
                canManageVisibleTasks={hasScopedManagementAccess}
                isLoading={isLoading}
                emptyStateLabel={t(TK.noTasksFound)}
                showResponsibleColumn={false}
                clients={clients}
                users={users}
                rowEdit={rowEdit}
                setRowEdit={setRowEdit}
                isSavingRow={isSavingRow}
                editingRowRef={editingRowRef}
                allowInlineEdit
                allowAssignedUserEdit={false}
                onStartEdit={startRowEdit}
                onSaveEdit={handleSaveRowEdit}
                onCancelEdit={() => setRowEdit(null)}
                onCompleteTask={setCompleteTask}
                onDismissTask={setDismissTask}
                onNavigateClient={navigateToClient}
            />

            {dismissTask && (
                <div style={{ border: '1px solid var(--surface-border-subtle)', borderRadius: 8, padding: 'var(--spacer-S)' }}>
                    <FormGroup>
                        <Label>{t(TK.dismissReason)}</Label>
                        <Input type="select" value={dismissReason} onChange={event => setDismissReason(event.target.value)}>
                            {dismissReasonOptions.map(option => <option key={option.value} value={option.value}>{t(option.labelKey)}</option>)}
                        </Input>
                    </FormGroup>
                    {dismissReason === TaskDismissReasons.other && (
                        <FormGroup>
                            <Label>{t(TK.reason)}</Label>
                            <Input value={dismissOtherReason} onChange={event => setDismissOtherReason(event.target.value)} />
                        </FormGroup>
                    )}
                    <FormGroup>
                        <Label>{t(TK.notes)}</Label>
                        <Input type="textarea" value={dismissNotes} onChange={event => setDismissNotes(event.target.value)} />
                    </FormGroup>
                    <div className="d-flex justify-content-end" style={{ gap: 'var(--spacer-XS)' }}>
                        <AppButton onClick={() => {
                            setDismissTask(null);
                            setDismissOtherReason('');
                            setDismissNotes('');
                            setDismissReason(TaskDismissReasons.notApplicable);
                        }}>{t(TK.cancel)}</AppButton>
                        <AppButton color="danger" onClick={() => handleDismissTask(dismissTask)}>{t(TK.dismissTask)}</AppButton>
                    </div>
                </div>
            )}

            <TaskCreateModal
                show={showCreate}
                onClose={() => setShowCreate(false)}
                onCreated={loadTasks}
                clients={clients}
                initialAssignedUserId={linkedUser.id}
                allowAssignedUserSelection={false}
                allowClientSelection
            />
            <TaskCompleteDialog
                task={completeTask}
                onClose={() => setCompleteTask(null)}
                onCompleted={loadTasks}
            />
        </div>
    );
};

export default ProfessionalTasksTable;
